import { useEffect } from "react";
import {
  useGetMany,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { Link } from "react-router";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { AsideSection } from "../misc/AsideSection";
import type { Company, Contact } from "../types";

/**
 * company_ids holds every linked company, the main one (company_id) included.
 * This input only shows the other ones, and keeps the main company in the list.
 */
export const OtherCompaniesInput = () => {
  const { getValues, setValue } = useFormContext();
  const mainCompanyId: Identifier | null | undefined = useWatch({
    name: "company_id",
  });

  useEffect(() => {
    if (mainCompanyId == null) return;
    const linked: Identifier[] = getValues("company_ids") ?? [];
    if (!linked.includes(mainCompanyId)) {
      setValue("company_ids", [mainCompanyId, ...linked], {
        shouldDirty: true,
      });
    }
  }, [mainCompanyId, getValues, setValue]);

  return (
    <ReferenceArrayInput
      source="company_ids"
      reference="companies"
      perPage={10}
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteArrayInput
        label="resources.contacts.fields.company_ids"
        optionText="name"
        helperText={false}
        format={(ids?: Identifier[]) =>
          (ids ?? []).filter((id) => id !== mainCompanyId)
        }
        parse={(ids?: Identifier[]) =>
          mainCompanyId == null
            ? (ids ?? [])
            : [
                mainCompanyId,
                ...(ids ?? []).filter((id) => id !== mainCompanyId),
              ]
        }
      />
    </ReferenceArrayInput>
  );
};

/** Aside section listing the contact's other companies, hidden when there are none. */
export const ContactOtherCompanies = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  const otherIds = (record?.company_ids ?? []).filter(
    (id) => id !== record?.company_id,
  );
  const { data: companies } = useGetMany<Company>(
    "companies",
    { ids: otherIds },
    { enabled: otherIds.length > 0 },
  );

  if (otherIds.length === 0) return null;

  return (
    <AsideSection title={translate("resources.contacts.fields.company_ids")}>
      {companies?.map((company) => (
        <Link
          key={company.id}
          to={`/companies/${company.id}/show`}
          className="flex items-center gap-2 hover:underline"
        >
          <CompanyAvatar record={company} width={20} height={20} />
          {company.name}
        </Link>
      ))}
    </AsideSection>
  );
};
